import {
  GetRechnungDetailsDto,
} from "../../../../api-client";
import {
  useDebitPermissions,
  useCreditPermissions,
  usePatientBillsInfo,
  usePatientRechnungen,
  usePatientTransactions,
} from "../../../../stores/Transactions/transaction.queries";
import { useMemo } from "react";

/**
 * Combines all patient billing data in one hook
 * Replaces the existing usePatientRechnungen hook from usePatientBills.ts
 */
export const usePatientBillingQueryData = (patientId: number) => {
  // Fetch all data in parallel
  const billsQuery = usePatientBillsInfo(patientId);
  const transactionsQuery = usePatientTransactions(patientId);
  const rechnungenQuery = usePatientRechnungen(patientId);
  const debitPermsQuery = useDebitPermissions(patientId!);
  const creditPermsQuery = useCreditPermissions(patientId!);

  // Build rechnungMap from patient-level rechnungen (no N+1)
  const rechnungMap = useMemo(() => {
    const map: Record<string, GetRechnungDetailsDto[]> = {};
    if (!rechnungenQuery.data) return map;

    for (const r of rechnungenQuery.data) {
      if (!r.rxId) continue; // skip unattached rechnungen
      (map[r.rxId] ||= []).push(r);
    }

    // Sort each bucket newest first
    Object.keys(map).forEach(
      (rxId) =>
      (map[rxId] = map[rxId]
        .slice()
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )),
    );
    return map;
  }, [rechnungenQuery.data]);

  // Combined loading and error states
  const isLoading =
    billsQuery.isLoading ||
    transactionsQuery.isLoading ||
    rechnungenQuery.isLoading;

  const error =
    billsQuery.error || transactionsQuery.error || rechnungenQuery.error;

  return {
    billsInfo: billsQuery.data ?? [],
    allTransactions: transactionsQuery.data ?? [],
    rechnungBills: rechnungenQuery.data ?? [],
    debitPermissions: debitPermsQuery.data ?? [],
    creditPermissions: creditPermsQuery.data ?? [],
    rechnungMap,

    isLoading,
    error,

    queries: {
      bills: billsQuery,
      transactions: transactionsQuery,
      rechnungen: rechnungenQuery,
      debitPermissions: debitPermsQuery,
      creditPermissions: creditPermsQuery,
    },

    refetch: {
      fetchPaidBills: billsQuery.refetch,
      fetchTransactions: transactionsQuery.refetch,
      fetchRechnungBills: rechnungenQuery.refetch,
      fetchAll: async () => {
        await Promise.all([
          billsQuery.refetch(),
          transactionsQuery.refetch(),
          rechnungenQuery.refetch(),
        ]);
      },
    },
  };
};
